import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import { saveAs } from 'file-saver';
import { learnApiClient } from './clients/learnApiClient';

export function parseFilename(
  disposition: string | undefined,
  fallback: string,
): string {
  if (!disposition) return fallback;

  const rfc5987 = disposition.match(
    /filename\*=UTF-8''([^;]+)/i,
  );
  if (rfc5987?.[1]) {
    try { return decodeURIComponent(rfc5987[1]); } catch { return fallback; }
  }

  const quoted = disposition.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];

  const bare = disposition.match(/filename=([^;\s]+)/i);
  if (bare?.[1]) return bare[1];

  return fallback;
}

interface DownloadCsvOptions {
  /** Defaults to learnApiClient — pass another registered client for
   * endpoints outside /learn/v3 (e.g. hubApiClient for /hub). */
  client?: AxiosInstance;
  /** Query params, forwarded to axios. */
  params?: AxiosRequestConfig['params'];
}

/** Returns the response headers so callers can read side-channel signals
 * (e.g. the hub export's X-Hub-Report-Truncated). */
export async function downloadCsv(
  path: string,
  fallbackFilename = 'report.csv',
  { client = learnApiClient, params }: DownloadCsvOptions = {},
): Promise<Record<string, string>> {
  const response = await client.get(path, { responseType: 'blob', params });
  const fileName = parseFilename(
    response.headers['content-disposition'],
    fallbackFilename,
  );
  saveAs(new Blob([response.data], { type: 'text/csv' }), fileName);

  return response.headers as Record<string, string>;
}
